import { Dtos, type ProConId, type SuretyAccountId } from "@inrev/common";
import { useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import {
	proConRelationshipsQueryKey,
	proConsQueryKey,
	retiredProConsQueryKey,
} from "../decisioning.keys";
import { type ProCon } from "../types";
import { invalidateListWhenIdle, useOptimisticListMutation } from "../useOptimisticListMutation";

/** Un-retires a pro/con: it leaves the retired list and joins the active one at the end of its bucket. */
export const useRestoreProCon = (accountId: SuretyAccountId) => {
	const { post } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useOptimisticListMutation<ProCon, ProConId, ProCon>({
		queryKey: retiredProConsQueryKey(accountId),
		request: async (id) =>
			Dtos.Decisioning.ProCon.Get.Response.schema.parse(
				await post<unknown>(`/v2/admin/surety/accounts/${accountId}/pro-cons/${id}/restore`),
			),
		apply: (items, id) => items.filter((item) => item.id !== id),
		affectedIds: (id) => [id],
		failureMessage: () => "Couldn't restore the factor.",
		reconcile: (items, restored) => {
			queryClient.setQueryData<ProCon[]>(
				proConsQueryKey(accountId),
				(active) => active && [...active.filter((item) => item.id !== restored.id), restored],
			);
			void invalidateListWhenIdle(queryClient, proConsQueryKey(accountId));
			void invalidateListWhenIdle(queryClient, proConRelationshipsQueryKey(accountId));
			return items.filter((item) => item.id !== restored.id);
		},
	});

	return {
		restoreProCon: mutation.mutate,
	};
};
